import { createSelector } from '@reduxjs/toolkit';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'preact/compat';
import { useCallback, useEffect } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { postUserAppOnboardedMutationFn } from '@/queries/users';
import { useSelector, useStore } from '@/redux/helper';
import { createSortedHabitatsSelector } from '@/redux/selectors/habitats';
import {
  selectActiveProductNames,
  selectHasExplorerFeature,
  selectHasGuidedExperienceFeature,
} from '@/redux/selectors/users';
import { updateUserProperty } from '@/redux/slices/user/actions';
import { trackMP } from '@/shared/mp';

import ExplorerDayPass from '../products/ExplorerDayPass';
import SingleHabitatDayPass from '../products/SingleHabitatDayPass';

const selectHabitat = createSelector(
  selectHasExplorerFeature,
  createSortedHabitatsSelector(['online', 'lastMediaCapture', 'index', 'currentViewers', 'title']),
  (hasExplorerFeature, habitats) => (hasExplorerFeature ? undefined : habitats?.[0]),
);

const AppOnboardingProductsOverview = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const isEducator = useSelector((state) => state.user.persona === 'educator');
  const hasGuidedExperienceFeature = useSelector(selectHasGuidedExperienceFeature);
  const hasExplorerFeature = useSelector(selectHasExplorerFeature);
  const [habitat, setHabitat] = useState(() => selectHabitat(store.getState()));

  const { mutate: markOnboarded } = useMutation({
    mutationFn: postUserAppOnboardedMutationFn,
    onSuccess: (data) => {
      dispatch(updateUserProperty(data));
    },
  });

  const finishOnboarding = useCallback(() => {
    markOnboarded();
    trackMP('after-signup-step', {
      step: 'productsOverview',
      activeProductNames: selectActiveProductNames(store.getState()),
    });
  }, [markOnboarded, store]);

  // only update the cached habitats if there are no habitats in the cache
  useEffect(() => {
    if (habitat?._id || hasExplorerFeature) {
      return undefined;
    }
    const unsubscribe = store.subscribe(() => {
      setHabitat(selectHabitat(store.getState()));
    });
    return () => unsubscribe();
  }, [store, habitat?._id, hasExplorerFeature]);

  // skip onboarding if user has explorer feature already, usually for bundling users
  useEffect(() => {
    if (hasGuidedExperienceFeature) {
      finishOnboarding();
    }
  }, [hasGuidedExperienceFeature, finishOnboarding]);

  if (hasGuidedExperienceFeature) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0">
        <h1 className="preflight preflight-h1 p-5 pb-4 pt-10 text-center text-2xl/7 font-bold text-[#162B1B]">
          How Do You Want to Visit?
        </h1>
      </div>
      <div className="min-h-0 grow overflow-y-auto px-8 pb-8">
        <div className="flex flex-col items-stretch justify-center gap-8 pt-4">
          <ExplorerDayPass variant="primary" forEducator={isEducator} entryPoint="onboarding" />
          {!hasExplorerFeature && habitat && <SingleHabitatDayPass habitatId={habitat._id} entryPoint="onboarding" />}
          <button
            type="button"
            className="preflight preflight-button text-grey-6 underline"
            onClick={() => finishOnboarding()}
          >
            {!hasExplorerFeature && habitat ? 'Choose Another Animal' : 'Dismiss'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppOnboardingProductsOverview;
